import { Fragment, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams, NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Paper, Stack, Tab, Tabs } from '@mui/material';
import { EditRounded } from '@mui/icons-material';
import { Roles } from '@/core/types';
import useAuth from '@/shared/context/auth-context';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import Backdrop from '@/components/ui/backdrop';
import { useSite } from '@/queries/sites';
import SiteForm from '@/features/sites/site-form';
import { SingleSiteResponse } from '@/api/sites/types';

export type SiteContext = { site: SingleSiteResponse | null };

const SitePage: FunctionComponent = (): ReactElement => {
	const queryClient = useQueryClient();

	const { user: authUser } = useAuth();
	const { role } = authUser || {};

	const location = useLocation();
	const { siteId } = useParams();
	const { queryKey: useSiteQueryKey, query: useSiteQuery } = useSite(siteId);
	const { data: site } = useSiteQuery;

	const [siteForm, setSiteForm] = useState({ open: false });

	const refetch = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(useSiteQueryKey);
	};

	const tabs = useMemo(
		() => ({
			'site-management': {
				label: 'Locations',
				path: `/sites/${String(siteId)}/locations`,
			},
			devices: {
				label: 'Devices',
				path: `/sites/${String(siteId)}/devices`,
			},
			users: {
				label: 'Users',
				path: `/sites/${String(siteId)}/users`,
			},
		}),
		[siteId]
	);

	const [currentTab, setCurrentTab] = useState(tabs['site-management'].path);

	useEffect(() => {
		if (location.pathname !== `/sites/${String(siteId)}`) setCurrentTab(location.pathname);
	}, [location.pathname, siteId]);

	if (!site) {
		return <Backdrop />;
	}

	const headerControls = (
		<Stack direction="row" spacing={1.5}>
			<Button
				variant="outlined"
				startIcon={<EditRounded />}
				onClick={() => setSiteForm({ ...siteForm, open: true })}
			>
				Edit
			</Button>
		</Stack>
	);

	return (
		<Fragment>
			<AdminPanelPage
				title={site.name}
				breadcrumbs={[{ title: 'Sites', path: '/sites' }, { title: site.name }]}
				headerControls={authUser?.role === Roles.ACCOUNT_ADMIN ? headerControls : null}
			>
				<Stack direction="column">
					<Tabs value={currentTab}>
						{Object.entries(tabs).map(([tabName, tab]) => (
							<Tab
								key={tabName}
								component={NavLink}
								label={tab.label}
								value={tab.path}
								to={tab.path}
							/>
						))}
					</Tabs>
					<Box component={Paper} padding={2.5}>
						<Outlet context={{ site } satisfies SiteContext} />
					</Box>
				</Stack>
			</AdminPanelPage>
			{role === Roles.ACCOUNT_ADMIN && (
				<SiteForm
					open={siteForm.open}
					onClose={() => setSiteForm({ open: false })}
					customer={authUser?.customer}
					site={site}
					subtitle={authUser?.customer?.name}
					onSubmitted={refetch}
				/>
			)}
		</Fragment>
	);
};

export default SitePage;
